// ---------------------------------------------------------------------------
// SkeletonRoute — page-shaped loading states for route-level loading.tsx
// ---------------------------------------------------------------------------
//
// Why a shape parameter instead of one generic skeleton.
//
// 93 PMS pages shared a single `SkeletonPage`, so every route flashed the same
// anonymous grey block regardless of what was actually arriving. A skeleton
// exists to reserve the layout the real content will occupy — that is what
// makes it feel like the page is loading rather than the app having stalled.
// One that does not match the destination causes a visible reflow the moment
// data lands, which reads as a second load.
//
// These compose the EXISTING primitives (`SkeletonKPI`, `SkeletonTable`) rather
// than inventing new markup, so spacing and card treatment stay identical to
// the real components and cannot drift apart from them.
//
// The variants below are the four real layouts in the PMS, taken from what the
// pages actually render — not invented categories:
//
//   'stats-table'  a KPI row above a data table   (reservations, guests, …)
//   'table'        a table with no KPI row        (audit log, groups, …)
//   'stats'        KPI cards only                 (dashboard, analytics)
//   'board'        column layout                  (front desk, housekeeping)
//   'tabs'         a tab bar over panel content    (loyalty, communications)
//
// 'calendar' was added for batch 6: RateCalendar is a month grid of date cells,
// which none of the other six resemble. A table skeleton would reserve rows of
// full-width bars where a 7-column grid arrives — a worse mismatch than the
// generic block, because it looks deliberate.
//
// 'form' was added for batch 4: KioskMode, GroupBookingForm and FolioDetail are
// input/card layouts with no KPI row and no table. Reserving a stats row above
// a form would push the first field down and then snap it back — the reflow
// these skeletons exist to prevent.
//
// 'tabs' was added for batch 2 rather than forcing those pages into one of the
// existing four. LoyaltyAdmin and CommunicationManager are tab shells — no KPI
// row and no table of their own — so 'stats' or 'table' would have reserved a
// layout that never arrives, producing exactly the reflow these skeletons exist
// to prevent.
// ---------------------------------------------------------------------------

import { Skeleton, SkeletonKPI, SkeletonTable } from './Skeleton';

export type RouteSkeletonShape = 'stats-table' | 'table' | 'stats' | 'board' | 'tabs' | 'form' | 'calendar';

interface SkeletonRouteProps {
  shape: RouteSkeletonShape;
  /** KPI cards to reserve. Match the real page so the row does not resize. */
  stats?: number;
  /** Table rows to reserve. Roughly a viewport's worth. */
  rows?: number;
  /** Table columns to reserve. Match the real table's column count. */
  cols?: number;
  /** Board columns, for the 'board' shape. */
  columns?: number;
}

/** Page title and subtitle — every PMS page has a PageHeader. */
function SkeletonHeader() {
  return (
    <div className="flex items-center justify-between">
      <div className="space-y-2">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-72" />
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-9 w-24 rounded-lg" />
        <Skeleton className="h-9 w-32 rounded-lg" />
      </div>
    </div>
  );
}

/** Kanban-style columns, for board pages. */
function SkeletonBoard({ columns }: { columns: number }) {
  return (
    <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {Array.from({ length: columns }).map((_, col) => (
        <div key={col} className="space-y-3">
          <Skeleton className="h-5 w-2/3" />
          {Array.from({ length: 3 }).map((_, card) => (
            <div key={card} className="rounded-xl border border-hms-border bg-hms-card p-4 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** A tab bar above panel content, for pages that are a tab shell. */
function SkeletonTabs() {
  return (
    <div className="space-y-4">
      <div className="flex gap-2 border-b border-hms-border pb-px">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-28 rounded-t-lg" />
        ))}
      </div>
      <div className="rounded-xl border border-hms-border bg-hms-card p-6 space-y-4">
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-4/6" />
      </div>
    </div>
  );
}

/** Stacked field rows in a card, for form and detail pages. */
function SkeletonForm() {
  return (
    <div className="rounded-xl border border-hms-border bg-hms-card p-6 space-y-5">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
      ))}
      <div className="flex gap-2 pt-2">
        <Skeleton className="h-10 w-28 rounded-lg" />
        <Skeleton className="h-10 w-24 rounded-lg" />
      </div>
    </div>
  );
}

/** A month grid of date cells, for calendar pages. */
function SkeletonCalendar() {
  return (
    <div className="rounded-xl border border-hms-border bg-hms-card p-4 space-y-3">
      <div className="grid grid-cols-7 gap-2">
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton key={i} className="h-4 w-full" />
        ))}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {Array.from({ length: 35 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-lg" />
        ))}
      </div>
    </div>
  );
}

/**
 * A loading state shaped like the page that is arriving.
 *
 * `animate-in fade-in` matches the real page's entrance, so the swap from
 * skeleton to content is not itself a visible flash.
 */
export function SkeletonRoute({
  shape,
  stats = 4,
  rows = 8,
  cols = 6,
  columns = 4,
}: SkeletonRouteProps) {
  return (
    <div className="space-y-6 p-6 animate-in fade-in duration-300" data-skeleton-shape={shape}>
      <SkeletonHeader />

      {(shape === 'stats' || shape === 'stats-table' || shape === 'board') && (
        <SkeletonKPI count={stats} />
      )}

      {(shape === 'table' || shape === 'stats-table') && (
        <SkeletonTable rows={rows} cols={cols} />
      )}

      {shape === 'board' && <SkeletonBoard columns={columns} />}

      {shape === 'tabs' && <SkeletonTabs />}

      {shape === 'form' && <SkeletonForm />}

      {shape === 'calendar' && <SkeletonCalendar />}
    </div>
  );
}
